/**
 * Shared helpers for the analytics insight tables.
 */

import {
  HEADER_CONTROL_BREAKPOINTS,
  type HeaderBreakpoint,
} from './constants';

/** Which of the collapsible header controls this report actually renders. */
export interface HeaderOverflowControls {
  hasTime: boolean;
  hasExport: boolean;
  /** Whether the Options control has any sections to show. */
  hasViewControls: boolean;
}

/**
 * Effective breakpoint per control. `null` means the control never collapses and
 * should render inline unconditionally.
 */
export interface HeaderOverflowLayout {
  time: HeaderBreakpoint | null;
  exportButton: HeaderBreakpoint | null;
  selector: HeaderBreakpoint | null;
  viewControls: HeaderBreakpoint | null;
  /** Width below which the "..." trigger shows. `null` means no menu at all. */
  overflowAt: HeaderBreakpoint | null;
}

/**
 * Decide where each header control lives at a given container width.
 *
 * Options collapses the moment the menu exists, and its sections render flat
 * inside it — a dropdown nested in a dropdown is worse than either. Because it
 * is always in there, the menu can never hold a lone item, so every other
 * control keeps its natural breakpoint.
 *
 * The exception is a report with no Options sections at all: nothing pads the
 * menu, so the widest control would sit in it alone. There the widest waits for
 * the next breakpoint down, which is also where the menu appears.
 */
export const getHeaderOverflowLayout = ({
  hasTime,
  hasExport,
  hasViewControls,
}: HeaderOverflowControls): HeaderOverflowLayout => {
  // Descending. The code set selector is always rendered.
  const present = [
    hasTime && HEADER_CONTROL_BREAKPOINTS.time,
    hasExport && HEADER_CONTROL_BREAKPOINTS.export,
    HEADER_CONTROL_BREAKPOINTS.selector,
  ].filter(Boolean) as HeaderBreakpoint[];

  const overflowAt = hasViewControls ? present[0] : present[1] ?? null;

  const effectiveFor = (breakpoint: HeaderBreakpoint) => {
    if (!present.includes(breakpoint)) return null;
    // Only one collapsible control, so there is nothing to collapse into.
    if (overflowAt === null) return null;
    return breakpoint === present[0] ? overflowAt : breakpoint;
  };

  return {
    time: effectiveFor(HEADER_CONTROL_BREAKPOINTS.time),
    exportButton: effectiveFor(HEADER_CONTROL_BREAKPOINTS.export),
    selector: effectiveFor(HEADER_CONTROL_BREAKPOINTS.selector),
    viewControls: hasViewControls ? overflowAt : null,
    overflowAt,
  };
};

/**
 * Drug codes are excluded from per-code insight columns: the ASP insight has no
 * configurable sale price for them, and Volume by Code mirrors ASP's columns.
 */
const isDrugCode = (value: string) => value?.toLowerCase().startsWith('drug');

/**
 * Flatten the selected code set(s) into the individual `CODESET-CODE` names that
 * become one column each (e.g. `CPT-55700`). These match the keys the
 * per-code insight endpoints return, so the same value is both column and key.
 *
 * Drug codes are dropped. A code set made up only of drug codes yields an empty
 * list, which the tables surface as a "no procedure or diagnosis codes" state.
 */
export const getCodeSetCodeColumns = (selectedCodeSet: any[]): string[] =>
  (selectedCodeSet || [])
    .flatMap(
      (codeSet) =>
        codeSet?.detailed_filters?.flatMap(
          (detailedFilter: any) => detailedFilter?.detailed_values || []
        ) || []
    )
    .map((detailedValue: any) => detailedValue?.value)
    .filter((value: string) => value && !isDrugCode(value));
